import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, ArrowLeft, BarChart3, CheckCircle2, ChevronRight, Eye, Percent, RefreshCw, Search, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DialogShell } from './ConfirmDialog';
import { apiClient, type VideoInsights } from '../lib/apiClient';

const muted = 'var(--text-secondary, #6b7280)';
const border = 'var(--theme-border, #e5e7eb)';
const primary = 'var(--theme-primary, #4f46e5)';
// The theme colour as text (readable where it matches the page, e.g. playful themes).
const primaryText = 'var(--primary-text, #4f46e5)';
const track = 'color-mix(in srgb, var(--text-secondary, #6b7280) 14%, transparent)';

const STYLES = `
@keyframes vi-shimmer { 0%, 100% { opacity: .45 } 50% { opacity: .9 } }
.vi-skeleton { animation: vi-shimmer 1.4s ease-in-out infinite; background: var(--theme-border, #e5e7eb); border-radius: 6px; }
.vi-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
@media (max-width: 560px) { .vi-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.vi-link { background: none; border: none; padding: 0; cursor: pointer; color: var(--theme-primary, #4f46e5); font-size: 13px; font-weight: 600; display: inline-flex; align-items: center; gap: 2px; }
.vi-link:hover { text-decoration: underline; }
.vi-band:hover .vi-band-fill { filter: brightness(1.12); }
.vi-search:focus-within { border-color: var(--theme-primary, #4f46e5); box-shadow: 0 0 0 3px color-mix(in srgb, var(--theme-primary, #4f46e5) 15%, transparent); }
`;

const sectionTitle = (text: string) => (
  <h3 style={{ margin: '22px 0 10px', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: muted }}>{text}</h3>
);

const Tile = ({ icon, label, value, hint }: { icon: ReactNode; label: string; value: string; hint?: string }) => (
  <div style={{ border: `1px solid ${border}`, borderRadius: 12, padding: '12px 14px', minWidth: 0 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: muted }}>
      <span aria-hidden style={{ display: 'inline-flex', color: primaryText }}>{icon}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
    <div style={{ fontSize: 24, fontWeight: 700, marginTop: 6, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{value}</div>
    {hint && <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>{hint}</div>}
  </div>
);

const initials = (name: string) =>
  name.split(/[\s._@-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || '?';

const Avatar = ({ name, size = 30, ring = false }: { name: string; size?: number; ring?: boolean }) => (
  <span aria-hidden title={name} style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: Math.round(size * 0.36), fontWeight: 700, letterSpacing: '-.02em', color: primaryText,
    background: 'color-mix(in srgb, var(--theme-primary, #4f46e5) 14%, var(--card-bg, #fff))',
    boxShadow: ring ? '0 0 0 2px var(--card-bg, #fff)' : undefined,
  }}>{initials(name)}</span>
);

const displayName = (v: VideoInsights['viewers'][number]) => v.name || v.email?.split('@')[0] || '—';

/**
 * The owner's viewer insights for one video. Sized for any audience: totals, how far viewers
 * got (four bands), and a compact viewers line; the full named list only on "See all".
 */
export default function VideoInsightsDialog({ videoId, videoTitle, onClose }: { videoId: string; videoTitle: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<VideoInsights | null>(null);
  const [failed, setFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setFailed(false);
    setData(null);
    const { data: result, error } = await apiClient.getVideoInsights(videoId);
    if (error || !result) setFailed(true);
    else setData(result);
  }, [videoId]);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.viewers ?? []).filter((v) => !q || displayName(v).toLowerCase().includes(q) || v.email?.toLowerCase().includes(q));
  }, [data, query]);

  const bands = [
    t('insights.band_0', 'Less than 25%'),
    t('insights.band_1', '25–50%'),
    t('insights.band_2', '50–75%'),
    t('insights.band_3', '75–100%'),
  ];

  const header = (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', paddingRight: 28 }}>
      {showAll ? (
        <button type="button" onClick={() => { setShowAll(false); setQuery(''); }} aria-label={t('insights.back', 'Back to insights')} style={{
          flexShrink: 0, width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${border}`, background: 'transparent', color: 'inherit', cursor: 'pointer',
        }}>
          <ArrowLeft size={18} />
        </button>
      ) : (
        <span aria-hidden style={{
          flexShrink: 0, width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'color-mix(in srgb, var(--theme-primary, #4f46e5) 10%, transparent)', color: primaryText,
        }}>
          <BarChart3 size={20} />
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
          {showAll ? t('insights.all_viewers', 'Signed-in viewers') : t('insights.title', 'Viewer insights')}
        </h2>
        <p style={{ margin: '2px 0 0', fontSize: 13, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{videoTitle}</p>
      </div>
    </div>
  );

  const summary = (d: VideoInsights) => {
    const maxBand = Math.max(1, ...d.distribution);
    const named = d.viewers;
    const shown = named.slice(0, 6);
    const others = named.length - Math.min(2, named.length);
    return (
      <>
        <div className="vi-tiles" style={{ marginTop: 18 }}>
          <Tile icon={<Eye size={14} />} label={t('insights.views', 'Views')} value={String(d.totals.views)} />
          <Tile icon={<Users size={14} />} label={t('insights.unique', 'Unique viewers')} value={String(d.totals.uniqueViewers)} />
          <Tile icon={<Percent size={14} />} label={t('insights.average', 'Avg. watched')} value={`${d.totals.averagePercent}%`} />
          <Tile icon={<CheckCircle2 size={14} />} label={t('insights.completed', 'Finished')} value={String(d.totals.completed)}
            hint={t('insights.completed_hint', 'watched 90%+')} />
        </div>

        {d.totals.uniqueViewers === 0 ? (
          <div style={{ textAlign: 'center', padding: '22px 16px', marginTop: 18, border: `1px dashed ${border}`, borderRadius: 12 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t('insights.empty_title', 'No viewers yet')}</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: muted }}>
              {t('insights.empty_hint', 'When people play this video, you will see who watched and how far.')}
            </p>
          </div>
        ) : (
          <>
            {/* How far viewers got: one hue, bar length = viewers, the count always written. */}
            {sectionTitle(t('insights.how_far', 'How far viewers got'))}
            <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {d.distribution.map((count, i) => (
                <div key={i} role="listitem" className="vi-band"
                  title={t('insights.band_title', '{{count}} viewers watched {{band}}', { count, band: bands[i] })}
                  style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 26%) 1fr 34px', gap: 12, alignItems: 'center', fontSize: 13 }}>
                  <span style={{ color: muted }}>{bands[i]}</span>
                  <span style={{ height: 10, borderRadius: 4, background: track, overflow: 'hidden' }}>
                    <span className="vi-band-fill" style={{
                      display: 'block', height: '100%', borderRadius: 4, background: primary,
                      width: `${(count / maxBand) * 100}%`, minWidth: count > 0 ? 6 : 0,
                    }} />
                  </span>
                  <span style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
                </div>
              ))}
            </div>

            {/* Viewers: a compact line; the full named list on "See all". */}
            {sectionTitle(t('insights.viewers', 'Viewers'))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {shown.length > 0 && (
                <div style={{ display: 'flex', paddingLeft: 3 }}>
                  {shown.map((v) => <span key={v.email} style={{ marginLeft: -3 }}><Avatar name={displayName(v)} ring /></span>)}
                </div>
              )}
              <p style={{ margin: 0, fontSize: 13, flex: 1, minWidth: 180 }}>
                {named.length === 0
                  ? t('insights.only_anonymous', '{{count}} anonymous viewers', { count: d.anonymous.count })
                  : <>
                      <strong>{named.slice(0, 2).map(displayName).join(', ')}</strong>
                      {others > 0 ? ` ${t('insights.and_others', 'and {{count}} others', { count: others })}` : ''}
                      {d.anonymous.count > 0 ? <span style={{ color: muted }}> · {t('insights.plus_anonymous', 'plus {{count}} anonymous', { count: d.anonymous.count })}</span> : null}
                    </>}
              </p>
              {named.length > 0 && (
                <button type="button" className="vi-link" onClick={() => setShowAll(true)}>
                  {t('insights.see_all', 'See all')} <ChevronRight size={14} aria-hidden />
                </button>
              )}
            </div>
          </>
        )}

        <p style={{ margin: '18px 0 0', fontSize: 12, color: muted }}>
          {d.since
            ? t('insights.since', 'Unique viewers and watch time since {{date}}. Each viewer counts once a day; your own plays are not counted.', { date: new Date(d.since).toLocaleDateString(undefined, { dateStyle: 'medium' }) })
            : t('insights.rules', 'Each viewer counts once a day; your own plays are not counted.')}
        </p>
      </>
    );
  };

  const allViewers = (
    <>
      {(data?.viewers.length ?? 0) > 8 && (
        <label className="vi-search" style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, padding: '8px 12px',
          border: `1px solid ${border}`, borderRadius: 10, transition: 'border-color .15s, box-shadow .15s',
        }}>
          <Search size={15} aria-hidden style={{ color: muted, flexShrink: 0 }} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} autoFocus
            placeholder={t('insights.search', 'Search viewers')} aria-label={t('insights.search', 'Search viewers')}
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: 'inherit', fontSize: 13 }} />
        </label>
      )}
      <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, maxHeight: '52vh', overflowY: 'auto' }}>
        {filtered.map((v) => {
          const name = displayName(v);
          return (
            <li key={v.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${border}` }}>
              <Avatar name={name} size={28} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div title={v.email ?? undefined} style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                <div style={{ fontSize: 12, color: muted }}>
                  {new Date(v.lastWatchedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                aria-label={t('insights.watched_of', '{{name}} watched {{pct}}%', { name, pct: v.percent })}>
                {v.percent}%
              </span>
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li style={{ padding: '20px 0', textAlign: 'center', fontSize: 13, color: muted }}>{t('insights.no_matches', 'No viewers match your search.')}</li>
        )}
      </ul>
      {data && data.anonymous.count > 0 && (
        <p style={{ margin: '12px 0 0', fontSize: 12, color: muted }}>
          {t('insights.anonymous_note', 'Plus {{count}} anonymous viewers (not listed).', { count: data.anonymous.count })}
        </p>
      )}
    </>
  );

  return (
    <DialogShell title={t('insights.title', 'Viewer insights')} onCancel={onClose} width={600}>
      <style>{STYLES}</style>
      {header}
      {failed ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '36px 16px', gap: 10 }}>
          <AlertTriangle size={22} aria-hidden style={{ color: muted }} />
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t('insights.load_failed', 'Could not load the insights.')}</p>
          <button type="button" onClick={() => void load()} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', border: 'none', background: primary, color: 'var(--button-primary-foreground, #fff)',
          }}>
            <RefreshCw size={14} aria-hidden /> {t('common.retry', 'Retry')}
          </button>
        </div>
      ) : !data ? (
        <div aria-busy="true" aria-label={t('loading', 'Loading…')} style={{ marginTop: 18 }}>
          <div className="vi-tiles">
            {[0, 1, 2, 3].map((i) => <div key={i} className="vi-skeleton" style={{ height: 78, borderRadius: 12 }} />)}
          </div>
          {[0, 1, 2, 3].map((i) => <div key={i} className="vi-skeleton" style={{ height: 10, marginTop: 16, width: `${80 - i * 15}%` }} />)}
        </div>
      ) : showAll ? allViewers : summary(data)}
    </DialogShell>
  );
}
